import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { isNumber, max, sumBy } from 'lodash';
import { PHASES_COLORS } from '../../../const';
import { VectorMeasurementResponseItem } from '../../../data-access/gql-types/graphql';
import { ecoLevels } from '../../../integrations/lavva/modules/channel-details/meter/current/utils';
import { IconArrowLink } from '../../../assets/icons';
import { IconTip } from '../../../assets/icons/icon-tip';
import { DonutChart } from '../chart';
import { DonutChartTwoDirections } from '../chart-two-direction';
import './index.scss';

type DonutChartWrapperProps = {
  lazyValue: number;
  value: number;
  averageValue: number;
  maxScaleValue: number;
  label: string;
  unit: string;
  phaseMeasurements: VectorMeasurementResponseItem[] | undefined;
  selectedPhases: number[];
  changeSelectedParameter?: () => void;
  isGreaterUnit?: boolean;
  noFixedScaleValues?: boolean;
  ticks?: boolean;
  indicator?: boolean;
  axis?: boolean;
  color?: string;
  arrowDirection?: 'up' | 'down';
  fixed?: number;
  arrow?: boolean;
  clickIndicator?: boolean;
  twoDirections?: boolean;
  ecoIndicator?: number | null;
  children?: React.ReactNode;
};

export const DonutChartWrapper: React.FC<DonutChartWrapperProps> = ({
  lazyValue,
  value,
  averageValue,
  ecoIndicator,
  maxScaleValue,
  label,
  unit,
  changeSelectedParameter,
  isGreaterUnit,
  phaseMeasurements,
  selectedPhases,
  children,
  noFixedScaleValues,
  ticks = true,
  indicator = true,
  axis = true,
  color,
  arrowDirection,
  arrow = false,
  fixed,
  clickIndicator = false,
  twoDirections,
}) => {
  const [clickableTip, setClickableTip] = useState<boolean>(!!clickIndicator);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setClickableTip(false);
    }, 8000);

    return () => clearTimeout(timeout);
  }, []);

  const getFixedValue = (value) => {
    if (noFixedScaleValues) return +value.toFixed(3);
    const power = Math.floor(value).toString().length;
    return power > 1 ? +value.toFixed(0) : +value.toFixed(1);
  };

  const summaryBalance = useMemo(() => {
    return {
      plus: sumBy(
        phaseMeasurements?.filter((x) => x.value > 0),
        'value',
      ),
      minus: sumBy(
        phaseMeasurements?.filter((x) => x.value < 0),
        'value',
      ),
    };
  }, [phaseMeasurements]);

  const isSummary = useMemo(() => {
    return !selectedPhases.length || (selectedPhases.length === 1 && selectedPhases[0] === 0);
  }, [selectedPhases]);

  const twoDirectionsMaxValue = useMemo(() => {
    const maxValue = max([Math.abs(summaryBalance.plus), Math.abs(summaryBalance.minus)]);
    const finalMaxVal = Math.ceil(maxValue ? maxValue + 2 : maxScaleValue * 2);
    if (isGreaterUnit === false && finalMaxVal <= 1000) return 1000;
    return finalMaxVal;
  }, [summaryBalance, isSummary, maxScaleValue, isGreaterUnit]);

  const roundScaleValue = (val: number) => {
    return Math.ceil(getFixedValue(val) / 10) * 10;
  };

  const scaleValues = useMemo(
    () =>
      !twoDirections
        ? [
            0,
            roundScaleValue(maxScaleValue) * 0.25,
            roundScaleValue(maxScaleValue) * 0.5,
            roundScaleValue(maxScaleValue) * 0.75,
            roundScaleValue(maxScaleValue) * 1,
          ]
        : [
            roundScaleValue(twoDirectionsMaxValue) * -1,
            roundScaleValue(twoDirectionsMaxValue) * -0.5,
            0,
            roundScaleValue(twoDirectionsMaxValue) * 0.5,
            roundScaleValue(twoDirectionsMaxValue) * 1,
          ],
    [maxScaleValue, noFixedScaleValues, twoDirections, twoDirectionsMaxValue],
  );

  const style = useMemo(() => {
    return isNumber(ecoIndicator)
      ? ({
          '--shadow': `3px 3px 15px rgba(${ecoLevels.find((level) => ecoIndicator <= level.maxValue)?.rgbValue}, 0.3)`,
        } as React.CSSProperties)
      : ({
          '--shadow': `5px 5px 15px rgba(0, 0, 0, 0.15)`,
        } as React.CSSProperties);
  }, [ecoIndicator]);

  const elementStyle = useMemo(() => {
    return selectedPhases.length > 1 || !selectedPhases.length
      ? null
      : ({
          '--color': PHASES_COLORS[selectedPhases[0]],
        } as React.CSSProperties);
  }, [selectedPhases]);

  return (
    <div className="donut-chart-wrapper">
      <div className="donut-chart-wrapper__donut">
        {!twoDirections ? (
          <DonutChart
            value={lazyValue}
            maxScaleValue={roundScaleValue(maxScaleValue)}
            averageValue={averageValue}
            phaseMeasurements={phaseMeasurements}
            selectedPhases={selectedPhases}
            ticks={ticks}
            indicator={indicator}
            color={color}
          />
        ) : (
          <DonutChartTwoDirections
            maxScaleValue={roundScaleValue(twoDirectionsMaxValue)}
            averageValue={averageValue}
            phaseMeasurements={phaseMeasurements}
            selectedPhases={selectedPhases}
            ticks={ticks}
            indicator={indicator}
            summaryBalance={summaryBalance}
          />
        )}
      </div>
      {axis && (
        <div className="donut-chart-wrapper__axis">
          {scaleValues.map((scaleValue, index) => {
            const isSummary = selectedPhases.find((x) => x === 0);

            return (
              <div
                {...(elementStyle ? { style: elementStyle } : {})}
                className={classNames('donut-chart-wrapper__axis-element', {
                  'donut-chart-wrapper__axis-element--active': scaleValue <= lazyValue && !twoDirections,
                  'donut-chart-wrapper__axis-element--active-red':
                    twoDirections && value > 0 && scaleValue <= value && scaleValue >= 0 && !isSummary,
                  'donut-chart-wrapper__axis-element--active-green':
                    twoDirections && value < 0 && scaleValue >= value && scaleValue <= 0 && !isSummary,
                })}
                key={index}
              >
                {scaleValue}
              </div>
            );
          })}
        </div>
      )}
      <div className="donut-chart-wrapper__donut-element">
        <div className="donut-chart-wrapper__donut-element-bg" style={style} />
        <div className="donut-chart-wrapper__donut-element-info" style={style} onClick={changeSelectedParameter}>
          {label && <div className="donut-chart-wrapper__donut-element-text">{label}</div>}
          <div className="donut-chart-wrapper__donut-element-wrapper">
            <div className="donut-chart-wrapper__donut-element-value">
              {value.toFixed(fixed !== undefined ? fixed : 1)}
            </div>
            <div className="donut-chart-wrapper__donut-element-unit">{unit}</div>
          </div>
          {arrow && (
            <div
              className={classNames('donut-chart-wrapper__donut-element-icon', {
                up: arrowDirection === 'up',
                down: arrowDirection === 'down',
              })}
            >
              <IconArrowLink className="icon-arrow-link--accent" size={22} />
            </div>
          )}
        </div>
        {clickableTip && <IconTip pulse />}
      </div>
      {children}
    </div>
  );
};
